import React, { useCallback, useEffect, useState } from 'react';

import saveFile from 'helpers/saveFile';
import { useDebounce } from 'hooks';
import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableStateEvent
} from 'primereact/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';

import { FeedbackService } from '../components/services/FeedbackService';
import { FeedbackFile, FeedbackUI } from '../types/interfaces';
import { formatTimestamp } from '../utils/date-utils';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

type FeedbackCardVariant = 'dialog' | 'page';

type FeedbackCardProps = {
  variant?: FeedbackCardVariant;
  scrollHeight?: string;
};

const DEFAULT_DIALOG_SCROLL_HEIGHT = '560px';
const DEFAULT_PAGE_FALLBACK_SCROLL_HEIGHT = 'calc(100vh - 240px)';

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  variant = 'dialog',
  scrollHeight: scrollHeightProp
}) => {
  const [attachmentsMap, setAttachmentsMap] = useState<
    Record<number, FeedbackFile[]>
  >({});
  const [feedbackData, setFeedbackData] = useState<FeedbackUI[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15); // Default rows per page
  const [computedScrollHeight, setComputedScrollHeight] = useState<string>(
    variant === 'dialog'
      ? DEFAULT_DIALOG_SCROLL_HEIGHT
      : DEFAULT_PAGE_FALLBACK_SCROLL_HEIGHT
  );
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    phoneNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const getFeedbackApi = useCallback(async (signal?: AbortSignal) => {
    try {
      const feedback = await FeedbackService.read(signal); // Передача signal
      if (feedback) {
        const feedbackWithTransformedDate = feedback.map((item) => ({
          ...item,
          date: formatTimestamp(new Date(item.date).getTime())
        }));
        setFeedbackData(feedbackWithTransformedDate);
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching feedback:', error.message);
        }
      } else {
        console.error('Unknown error:', error);
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        if (isMounted) {
          await getFeedbackApi(abortController.signal);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false; // Флаг предотвращает выполнение при размонтировании
      abortController.abort();
    };
  }, [getFeedbackApi]);

  const fetchAttachmentsForPage = useCallback(async () => {
    const start = currentPage * rowsPerPage;
    const end = start + rowsPerPage;
    const currentFeedback = feedbackData.slice(start, end);

    const chunkSize = 5; // Process in chunks to reduce blocking
    for (let i = 0; i < currentFeedback.length; i += chunkSize) {
      const feedbackChunk = currentFeedback.slice(i, i + chunkSize);

      const promises = feedbackChunk
        .filter((item) => !attachmentsMap[item.id]) // Exclude already loaded
        .map((item) =>
          FeedbackService.getFilesByFeedback(item.id).then((files) => ({
            feedbackId: item.id,
            files
          }))
        );

      const attachments = await Promise.all(promises);
      setAttachmentsMap((prev) => {
        const updatedMap = { ...prev };
        attachments.forEach(({ feedbackId, files }) => {
          updatedMap[feedbackId] = files;
        });
        return updatedMap;
      });
    }
  }, [currentPage, rowsPerPage, feedbackData, attachmentsMap]);

  useEffect(() => {
    if (feedbackData.length > 0) {
      fetchAttachmentsForPage();
    }
  }, [feedbackData, currentPage, rowsPerPage]);

  const { debouncedCallback: debouncedFetchAttachmentsForPage } = useDebounce(
    fetchAttachmentsForPage,
    300
  );

  const renderAttachments = useCallback(
    (rowData: FeedbackUI) => {
      const isDoubleClick = false;
      const attachments = attachmentsMap[rowData.id] || [];
      const imageFormats = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg']);
      let clickTimer: ReturnType<typeof setTimeout> | null = null; // Timeout for distinguishing single and double clicks

      const handleFileDownload = async (file: FeedbackFile) => {
        const result = await FeedbackService.getFeedbackFileById(file.id);
        const contentType =
          result.headers['content-type'] || 'application/octet-stream';
        const fileName = file.name.includes('.')
          ? file.name
          : `${file.name}.${contentType.split('/')[1]}`;
        await saveFile(result.data, fileName, contentType);
      };

      const handleFileOpen = async (file: FeedbackFile) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (imageFormats.has(fileExtension || '')) {
          const result = await FeedbackService.getFeedbackFileById(file.id);
          const blob = new Blob([result.data], {
            type: `image/${fileExtension}`
          });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      };

      const handleClick = (file: FeedbackFile) => {
        if (clickTimer) clearTimeout(clickTimer); // Clear any pending timer

        clickTimer = setTimeout(async () => {
          // Load all files (including images) on single click
          if (!isDoubleClick) await handleFileDownload(file);
        }, 300); // Delay for distinguishing single click
      };

      const handleDoubleClick = (file: FeedbackFile) => {
        if (clickTimer) clearTimeout(clickTimer); // Cancel single-click action if double-clicked
        clickTimer = null; // Ensure the timer is not used again

        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (imageFormats.has(fileExtension || '')) {
          // Open image files in a new tab on double click
          handleFileOpen(file);
        }
      };

      return (
        <div>
          {attachments.map((file) => (
            <div
              key={file.id}
              className="attachment-row"
              onClick={() => handleClick(file)}
              onDoubleClick={() => handleDoubleClick(file)}
              style={{ cursor: 'pointer' }}
            >
              <span className="file-name">{file.name}</span>
              <i className="pi pi-download ml-2"></i>
            </div>
          ))}
        </div>
      );
    },
    [attachmentsMap]
  );

  useEffect(() => {
    if (feedbackData.length > 0) {
      debouncedFetchAttachmentsForPage();
    }
  }, [
    currentPage,
    rowsPerPage,
    feedbackData,
    debouncedFetchAttachmentsForPage
  ]);

  const onPageChange = (event: DataTableStateEvent) => {
    setCurrentPage(event.first / event.rows);
    setRowsPerPage(event.rows);
  };

  useEffect(() => {
    if (scrollHeightProp) return;
    if (variant !== 'page') {
      setComputedScrollHeight(DEFAULT_DIALOG_SCROLL_HEIGHT);
      return;
    }

    const recalc = () => {
      const tableHead = document.querySelector(
        '.feedback-card .p-datatable-thead'
      );
      const paginatorElement = document.querySelector(
        '.feedback-card .p-paginator'
      );
      const paginatorHeight =
        paginatorElement instanceof HTMLElement
          ? paginatorElement.offsetHeight
          : 0;

      if (!(tableHead instanceof HTMLElement)) {
        setComputedScrollHeight(DEFAULT_PAGE_FALLBACK_SCROLL_HEIGHT);
        return;
      }

      const rect = tableHead.getBoundingClientRect();
      const tableOffset =
        rect.top + (window.pageYOffset || document.documentElement.scrollTop);
      setComputedScrollHeight(`calc(100vh - ${tableOffset + paginatorHeight}px)`);
    };

    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [
    scrollHeightProp,
    variant,
    feedbackData.length,
    rowsPerPage,
    currentPage,
    loading
  ]);

  const scrollHeight =
    scrollHeightProp ??
    (variant === 'page' ? computedScrollHeight : DEFAULT_DIALOG_SCROLL_HEIGHT);

  return (
    <div
      className={
        'feedback-card' + (variant === 'dialog' ? ' dialog-tabless-card' : '')
      }
    >
      {loading ? (
        <ProgressSpinner />
      ) : (
        <DataTable
          filters={filters}
          selectionMode="single"
          metaKeySelection={false}
          scrollable
          scrollHeight={scrollHeight}
          sortMode="multiple"
          removableSort
          size="small"
          paginator
          value={feedbackData}
          rows={rowsPerPage}
          first={currentPage * rowsPerPage}
          onPage={onPageChange}
          rowsPerPageOptions={[5, 10, 15, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
          currentPageReportTemplate="{currentPage} из {totalPages}"
          dataKey="id"
          filterDisplay="row"
          emptyMessage="Обратная связь не найдена."
        >
          <Column
            sortable
            filter
            filterPlaceholder="Поиск по дате"
            header="Дата"
            columnKey="date"
            field="date"
            style={{ width: '10%', minWidth: '10%' }}
          />
          <Column
            sortable
            filter
            filterPlaceholder="Поиск по ФИО"
            header="ФИО"
            columnKey="name"
            field="name"
            style={{ width: '18%', minWidth: '18%' }}
          />
          <Column
            sortable
            filter
            filterPlaceholder="Поиск по номеру телефона"
            header="Номер телефона"
            columnKey="phoneNumber"
            field="phoneNumber"
            style={{ width: '11%', minWidth: '11%' }}
          />
          <Column
            sortable
            filter
            filterPlaceholder="Поиск по электронной почте"
            header="Электронная почта"
            columnKey="email"
            field="email"
            style={{ width: '17%', minWidth: '17%' }}
          />
          <Column
            sortable
            filter
            filterPlaceholder="Поиск по соообщениям"
            header="Сообщение"
            columnKey="description"
            field="description"
            style={{ width: variant === 'dialog' ? '33%' : '22%', minWidth: variant === 'dialog' ? '33%' : '22%' }}
          />
          <Column
            filterPlaceholder="Поиск по вложениям"
            header="Вложения"
            columnKey="attachments"
            body={renderAttachments}
            style={{ width: variant === 'dialog' ? '11%' : '22%', minWidth: variant === 'dialog' ? '11%' : '22%' }}
          />
        </DataTable>
      )}
    </div>
  );
};

FeedbackCard.displayName = 'FeedbackCard';

export default FeedbackCard;
